// DesignPreviewPage.jsx — Standalone mockup of the proposed "Hybrid Light/Dark" design.
// Reachable at /preview-design. Does NOT touch any existing component.

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, TrendingUp, Landmark, Wallet, ArrowRight, ChevronRight, MapPin, Clock, Users, ShieldCheck, Lock, Award } from "lucide-react";

const DEFAULT_HERO = "/images/hero-default.webp";

export default function DesignPreviewPage() {
  useEffect(() => { document.title = "VMBS – Design-Preview (Mockup)"; }, []);

  return (
    <div className="min-h-screen bg-[#F8F6F0] text-[#1A2530] font-sans" data-testid="design-preview">
      {/* HEADER (Glassmorphism, adaptive) */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-7xl mx-auto h-16 sm:h-20 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-sm bg-[#0C1620] flex items-center justify-center">
              <span className="font-heading text-base text-[#C5993A] font-semibold">V</span>
            </div>
            <span className="font-heading text-lg sm:text-xl tracking-[0.18em] text-[#0C1620] font-medium hidden sm:inline">
              V<span className="text-[#C5993A]">·</span>M<span className="text-[#C5993A]">·</span>B<span className="text-[#C5993A]">·</span>S
            </span>
          </div>
          <nav className="hidden lg:flex items-center gap-8 text-[12px] uppercase tracking-[0.18em] text-[#1A2530] font-medium">
            <a className="hover:text-[#C5993A] transition-colors">Warum Edelmetalle</a>
            <a className="hover:text-[#C5993A] transition-colors">Veranstaltungen</a>
            <a className="hover:text-[#C5993A] transition-colors">Kontakt</a>
            <a className="hover:text-[#C5993A] transition-colors">Newsletter</a>
            <a className="hover:text-[#C5993A] transition-colors">Karriere</a>
          </nav>
          <div className="flex items-center gap-3">
            <button className="text-[11px] uppercase tracking-[0.18em] text-[#5A6B7C] hover:text-[#1A2530] transition-colors">DE / EN</button>
            <Link to="/" className="text-[11px] uppercase tracking-[0.18em] text-[#C5993A] hover:underline">← Zurück</Link>
          </div>
        </div>
      </header>

      {/* HERO (preserved dark for impact) */}
      <section className="relative min-h-[100svh] flex items-center pt-20 bg-[#0C1620] overflow-hidden">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img src={DEFAULT_HERO} alt="Goldbarren" className="w-full h-full object-cover opacity-90 animate-ken-burns" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0C1620]/95 via-[#0C1620]/85 to-[#0C1620]/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C1620] via-transparent to-[#0C1620]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <p className="uppercase text-[11px] tracking-[0.28em] text-[#C5993A] mb-6 font-medium">
              Vermögensschutz mit Edelmetallen
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tight font-light text-white leading-[1.1] mb-7">
              Ihr Geld verliert.{" "}
              <span className="block text-[#C5993A] text-3xl sm:text-4xl lg:text-6xl whitespace-nowrap">Jeden Tag. Still. Unbemerkt.</span>
            </h1>
            <p className="text-base sm:text-lg text-[#F8F6F0]/85 leading-relaxed max-w-2xl mb-10 font-light">
              Physische Edelmetalle sind die einzige Anlageklasse, die gleichzeitig Kaufkraft erhält, steuerfrei wächst, sofort liquidierbar ist und Ihr Vermögen außerhalb des Bankensystems schützt.
            </p>
            <div className="flex flex-wrap gap-4 mb-12">
              <button className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#C5993A] text-white text-sm uppercase tracking-[0.15em] hover:bg-[#A67C2B] transition-colors rounded-sm shadow-[0_4px_24px_0_rgba(197,153,58,0.35)]">
                Beratung anfragen <ArrowRight className="w-4 h-4" />
              </button>
              <button className="inline-flex items-center gap-2 px-7 py-3.5 bg-transparent border border-[#C5993A] text-[#C5993A] text-sm uppercase tracking-[0.15em] hover:bg-[#C5993A]/10 transition-colors rounded-sm">
                Mehr erfahren
              </button>
            </div>
            <div className="mt-12">
              <p className="text-[#C5993A]/80 italic font-bold font-heading text-base sm:text-lg" style={{ fontWeight: 700 }}>
                „Der beste Zeitpunkt, Vermögen zu schützen, war gestern. Der nächstbeste ist heute."
              </p>
              <div className="flex items-center gap-3 mt-3">
                <span className="block h-px w-10 bg-gradient-to-r from-[#C5993A]/70 to-transparent" />
                <span className="text-[#C5993A]/60 text-xs uppercase tracking-[0.2em] font-light">
                  Chinesisches Sprichwort, adaptiert
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WARUM EDELMETALLE — Light section (the key change!) */}
      <section className="py-20 sm:py-28 lg:py-32 bg-[#F8F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <p className="uppercase text-[11px] tracking-[0.28em] text-[#C5993A] mb-4 font-medium">Warum Edelmetalle</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#0C1620] leading-[1.15]">
              4 unschlagbare Argumente
            </h2>
            <div className="mt-6 h-px w-16 bg-[#C5993A]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {[
              { icon: TrendingUp, hl: "+1.400 %", title: "Kaufkraft erhalten & Inflation schlagen", text: "Der Euro hat seit 2000 rund 45 % seiner Kaufkraft verloren. Gold legte im selben Zeitraum +1.400 % zu — von 262 €/oz auf heute ca. 4.000 €/oz." },
              { icon: Shield, hl: "Schweizer Zollfreilager", title: "Bankenunabhängig & vor staatlichem Zugriff geschützt", text: "Ihr Edelmetall wird auf Ihren Namen im Schweizer Zollfreilager gelagert — außerhalb der EU, außerhalb des Bankensystems. Kein Bail-in." },
              { icon: Landmark, hl: "0 % Steuer", title: "Steuerfrei kaufen & Gewinne steuerfrei vereinnahmen", text: "Physische Edelmetalle im Zollfreilager: 0 % MwSt. beim Kauf. Kursgewinne nach 12 Monaten: 0 % Abgeltungssteuer (§23 EStG)." },
              { icon: Wallet, hl: "Jederzeit verfügbar", title: "Liquider Wertspeicher", text: "Vermögenswert jederzeit einsehbar. Verkauf oder physische Auslieferung jederzeit möglich. Standardformate sichern Sofort-Marktfähigkeit." },
            ].map((arg, i) => {
              const Icon = arg.icon;
              return (
                <div key={i} className="group relative bg-white border border-black/5 rounded-lg p-7 sm:p-9 hover:shadow-[0_8px_32px_-12px_rgba(12,22,32,0.18)] hover:-translate-y-0.5 transition-all duration-300">
                  <span className="absolute top-3 right-5 font-heading text-[110px] leading-none text-[#C5993A]/8 font-semibold select-none pointer-events-none">
                    0{i + 1}
                  </span>
                  <div className="relative z-10">
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-11 h-11 rounded-sm bg-[#C5993A]/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#C5993A]" />
                      </div>
                      <span className="font-heading text-base text-[#C5993A] font-semibold tracking-wide">{arg.hl}</span>
                    </div>
                    <h3 className="font-heading text-xl lg:text-2xl text-[#0C1620] font-medium mb-3 leading-snug">{arg.title}</h3>
                    <p className="text-[#5A6B7C] leading-relaxed text-sm lg:text-base font-light">{arg.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[#F2EBE1] py-10 border-y border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-center gap-10 text-[#5A6B7C]">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-5 h-5 text-[#C5993A]" />
            <span className="text-xs uppercase tracking-[0.18em] font-medium">Schweizer Zollfreilager</span>
          </div>
          <span className="hidden md:block w-px h-5 bg-black/10" />
          <div className="flex items-center gap-2.5">
            <Award className="w-5 h-5 text-[#C5993A]" />
            <span className="text-xs uppercase tracking-[0.18em] font-medium">LBMA-zertifiziert</span>
          </div>
          <span className="hidden md:block w-px h-5 bg-black/10" />
          <div className="flex items-center gap-2.5">
            <Lock className="w-5 h-5 text-[#C5993A]" />
            <span className="text-xs uppercase tracking-[0.18em] font-medium">DSGVO-konform</span>
          </div>
          <span className="hidden md:block w-px h-5 bg-black/10" />
          <div className="flex items-center gap-2.5">
            <Users className="w-5 h-5 text-[#C5993A]" />
            <span className="text-xs uppercase tracking-[0.18em] font-medium">Persönliche Beratung</span>
          </div>
        </div>
      </section>

      {/* VERANSTALTUNGEN — Light */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <p className="uppercase text-[11px] tracking-[0.28em] text-[#C5993A] mb-4 font-medium">Termine</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-light text-[#0C1620]">Aktuelle Veranstaltungen</h2>
          </div>
          <ul className="space-y-3">
            {[
              { date: "15", month: "Jun 2026", title: "Webinar: Warum Edelmetalle in 2026 unverzichtbar sind", meta: ["15.06.2026, 19:00 Uhr", "Online", "Min. 5", "9 freie Plätze"] },
              { date: "22", month: "Jun 2026", title: "Exklusiv-Beratung in München", meta: ["22.06.2026, 14:00 Uhr", "München", "Min. 3", "6 freie Plätze"] },
            ].map((e, i) => (
              <li key={i} className="group bg-white border-l-4 border-transparent hover:border-[#C5993A] border-t border-r border-b border-black/5 rounded-r-md p-5 transition-all cursor-pointer hover:translate-x-1 hover:shadow-sm">
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-14 text-center">
                    <span className="block font-heading text-3xl text-[#C5993A] font-semibold leading-none">{e.date}</span>
                    <span className="block text-[10px] uppercase tracking-wider text-[#5A6B7C] mt-1">{e.month}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg text-[#0C1620] font-medium">{e.title}</h3>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-[#5A6B7C]">
                      {e.meta.map((m, j) => (
                        <span key={j} className="flex items-center gap-1.5">
                          {j === 0 && <Clock className="w-3.5 h-3.5 text-[#C5993A]" />}
                          {j === 1 && <MapPin className="w-3.5 h-3.5 text-[#C5993A]" />}
                          {j === 2 && <Users className="w-3.5 h-3.5 text-[#C5993A]" />}
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#C5993A] flex-shrink-0 mt-1 transition-transform group-hover:translate-x-1" />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* KUNDENSTIMMEN (NEW – on champagne) */}
      <section className="py-20 sm:py-24 bg-[#F2EBE1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="uppercase text-[11px] tracking-[0.28em] text-[#C5993A] mb-3 font-medium">Vertrauen</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-light text-[#0C1620]">Was unsere Kunden sagen</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "Diskret, kompetent und auf höchstem Niveau. Genau das, was ich von einer Vermögensverwaltung erwarte.", author: "Dr. H. Meyer", role: "Unternehmer, Bayern" },
              { quote: "Endlich eine Lösung, die Substanz hat. Steuervorteil und Bankenunabhängigkeit haben mich überzeugt.", author: "M. Schäfer", role: "Privatinvestor" },
              { quote: "Die persönliche Beratung war erstklassig. Man fühlt sich vom ersten Gespräch an verstanden.", author: "Familie K.", role: "Stuttgart" },
            ].map((t, i) => (
              <figure key={i} className="bg-white rounded-lg p-7 shadow-[0_4px_20px_-12px_rgba(12,22,32,0.15)] border border-black/5">
                <span className="font-heading text-4xl text-[#C5993A] leading-none">„</span>
                <blockquote className="text-[#1A2530] font-light leading-relaxed mt-2 mb-5">{t.quote}</blockquote>
                <figcaption>
                  <p className="font-medium text-[#0C1620] text-sm">{t.author}</p>
                  <p className="text-[#5A6B7C] text-xs">{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* KONTAKT — Back to dark */}
      <section className="py-20 sm:py-24 bg-[#0C1620]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <p className="uppercase text-[11px] tracking-[0.28em] text-[#C5993A] mb-3 font-medium">Kontakt</p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-light text-white mb-6">Persönliches Gespräch vereinbaren</h2>
              <p className="text-[#F8F6F0]/70 font-light leading-relaxed mb-6">
                Unsere Berater nehmen sich Zeit für Sie. Persönlich. Diskret. Unverbindlich.
              </p>
              <div className="flex items-center gap-3 mt-8 text-[#F8F6F0]/80 text-sm">
                <Lock className="w-4 h-4 text-[#C5993A]" />
                <span className="font-light">SSL-verschlüsselt · DSGVO-konform</span>
              </div>
            </div>
            <form className="space-y-5">
              {[
                { l: "Name *", t: "text", v: "" },
                { l: "E-Mail *", t: "email", v: "" },
                { l: "Telefon", t: "tel", v: "" },
              ].map((f, i) => (
                <label key={i} className="block">
                  <span className="block text-[10px] uppercase tracking-[0.2em] text-[#C5993A]/70 mb-2">{f.l}</span>
                  <input type={f.t} className="w-full bg-transparent border-0 border-b border-[#C5993A]/30 text-white focus:border-[#C5993A] focus:ring-0 px-0 py-2 outline-none transition-colors" placeholder="" />
                </label>
              ))}
              <label className="block">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-[#C5993A]/70 mb-2">Nachricht</span>
                <textarea rows={3} className="w-full bg-transparent border-0 border-b border-[#C5993A]/30 text-white focus:border-[#C5993A] focus:ring-0 px-0 py-2 outline-none transition-colors resize-none" />
              </label>
              <button type="button" className="inline-flex items-center gap-2 px-7 py-3 bg-[#C5993A] text-white text-sm uppercase tracking-[0.15em] hover:bg-[#A67C2B] transition-colors rounded-sm">
                Anfrage senden <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER with trust badges */}
      <footer className="bg-[#0C1620] border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#F8F6F0]/50">
            <p>© VMBS UG · Impressum · Datenschutz</p>
            <p className="flex items-center gap-2 italic font-light">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Design-Preview-Mockup (nicht produktiv)
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
